import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, fmtBRL, fmtDate, STATUS_LABELS, STATUS_CLASS, PLAN_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ShieldCheck, House, Receipt, Copy, Download, PenNib, FileText, Certificate,
  Warning, CheckCircle, LockKey, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoicePayment } from "@/components/InvoicePayment";

export default function TenantPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("contract");
  const [payFor, setPayFor] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [signature, setSignature] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/portal/${token}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.status === 403 ? e.response.data.detail : "Link inválido ou expirado.");
    }
  };
  useEffect(() => { load(); }, [token]);

  if (err) return <PortalError message={err} />;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando…</div>;

  const c = data.contract;
  const openInvoice = data.invoices.find((i) => i.status !== "pago");
  const isPending = c.status === "aguardando_assinatura";

  const pay = async (method) => {
    if (!payFor) return;
    try {
      await api.post(`/portal/${token}/pay/${payFor.invoice_id}`, { method });
      toast.success(`Pagamento via ${method.toUpperCase()} confirmado`);
      setPayFor(null);
      load();
    } catch { toast.error("Erro no pagamento"); }
  };

  const syncInvoice = async (inv) => {
    try {
      const { data } = await api.post(`/portal/${token}/invoices/${inv.invoice_id}/sync`);
      setPayFor(data);
      toast[data.status === "pago" ? "success" : "info"](data.status === "pago" ? "Pagamento confirmado!" : "Pagamento ainda não identificado");
      load();
    } catch { toast.error("Não foi possível atualizar"); }
  };

  const sign = async () => {
    if (!signature.trim()) { toast.error("Digite seu nome"); return; }
    try {
      await api.post(`/portal/${token}/sign`, { signature });
      toast.success("Contrato assinado! Boleto de ativação gerado.");
      setSignOpen(false); setSignature("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na assinatura");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bone)]">
      {/* Header */}
      <header className="bg-white border-b border-[var(--line)]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_locacao-shield/artifacts/5k0zetdw_logo%20safimob%2003.JPG" alt="Safimob" className="h-9" />
          <div className="text-xs text-slate-500 flex items-center gap-1.5"><LockKey size={14} /> Área do inquilino · sessão segura</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Hero card */}
        <div className="card-flat p-8 mb-6 relative overflow-hidden" data-testid="portal-hero">
          <div className="absolute right-6 top-6 opacity-5"><ShieldCheck size={140} weight="duotone" /></div>
          <div className="eyebrow mb-2">Olá,</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter">{c.tenant_name}</h1>
          <div className="text-slate-500 mt-2 mono text-sm">{c.tenant_cpf}</div>
          <div className="mt-4"><span className={`status-pill ${STATUS_CLASS[c.status]}`}>{STATUS_LABELS[c.status]}</span></div>

          {isPending && (
            <div className="mt-6 p-4 rounded-md bg-[var(--amber-soft)] border border-amber-200 flex items-center gap-3" data-testid="portal-pending">
              <Warning size={22} color="#D97706" weight="duotone" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900">Sua garantia aguarda assinatura.</div>
                <div className="text-amber-800/80">Revise o contrato abaixo e assine para ativar sua cobertura.</div>
              </div>
              <Button className="btn-primary" data-testid="portal-sign-btn" onClick={() => setSignOpen(true)}>
                <PenNib size={14} className="mr-2" /> Assinar agora
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-[var(--line)]" data-testid="portal-tabs">
          {[
            { id: "contract", label: "Meu Contrato", icon: FileText },
            { id: "invoices", label: "Faturas", icon: Receipt },
            { id: "certificate", label: "Certificado", icon: Certificate },
          ].map((t) => (
            <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === t.id ? "border-[var(--emerald)] text-[var(--emerald)]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Contract tab */}
        {tab === "contract" && (
          <div className="card-flat p-8" data-testid="portal-contract">
            <div className="eyebrow mb-1">Contrato de garantia locatícia</div>
            <div className="font-display text-2xl mb-6">Nº {c.contract_id.toUpperCase()}</div>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <div className="eyebrow mb-2">Imóvel</div>
                <div className="flex items-start gap-2"><House size={18} className="mt-0.5 text-slate-400" /> {c.property_address}</div>
                <div className="text-xs text-slate-500 mt-1">Proprietário: {c.owner_name}</div>
              </div>
              <div>
                <div className="eyebrow mb-2">Plano</div>
                <div className="font-display text-xl">{PLAN_LABELS[c.plan]}</div>
                <div className="text-xs text-slate-500 mt-1">Cobertura até <strong>{c.coverage_multiplier}x</strong> = {fmtBRL(c.rent_amount * c.coverage_multiplier)}</div>
              </div>
              <Info label="Aluguel" value={fmtBRL(c.rent_amount)} />
              <Info label="Condomínio" value={fmtBRL(c.condo_amount)} />
              <Info label="IPTU" value={fmtBRL(c.iptu_amount)} />
              <Info label="Prêmio Safimob (mensal)" value={fmtBRL(c.monthly_premium)} hi />
            </div>

            <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-100 text-xs text-slate-600">
              <strong>O que você paga aqui:</strong> apenas o <strong>prêmio da garantia</strong> ({fmtBRL(c.monthly_premium)}/mês) referente à cobertura Safimob.
              O aluguel, condomínio e IPTU continuam sendo pagos diretamente à sua imobiliária/proprietário.
            </div>

            <div className="border-t border-[var(--line)] mt-8 pt-6 text-xs text-slate-600 leading-relaxed">
              <p><strong>Objeto:</strong> A SAFIMOB GARANTIDORA DE ALUGUEL presta garantia locatícia para o imóvel acima, cobrindo aluguel, condomínio, IPTU e demais itens conforme plano contratado. A garantia entra em vigor com a assinatura eletrônica e o pagamento da 1ª parcela de ativação.</p>
              <p className="mt-2"><strong>Vigência:</strong> {c.duration_months} meses. Renovação automática após aviso.</p>
              {c.signed_at && (
                <p className="mt-3 text-[var(--emerald)]"><CheckCircle size={12} className="inline mr-1" /> Assinado eletronicamente em {fmtDate(c.signed_at)} — hash: <span className="mono">{c.signature_hash}</span></p>
              )}
            </div>
          </div>
        )}

        {/* Invoices tab */}
        {tab === "invoices" && (
          <div className="card-flat p-6" data-testid="portal-invoices">
            {openInvoice && (
              <div className="mb-6 p-6 rounded-lg bg-[var(--emerald)] text-white" data-testid="portal-open-invoice">
                <div className="eyebrow text-white/60">Próxima fatura</div>
                <div className="flex items-baseline gap-3 mt-1">
                  <div className="metric-value text-4xl">{fmtBRL(openInvoice.amount)}</div>
                  <div className="text-white/70 text-sm">vence em {fmtDate(openInvoice.due_date)}</div>
                </div>
                <div className="mt-4"><span className={`status-pill ${STATUS_CLASS[openInvoice.status]}`}>{STATUS_LABELS[openInvoice.status]}</span></div>
                <Button data-testid="portal-pay-btn" onClick={() => setPayFor(openInvoice)} className="bg-white text-[var(--emerald)] hover:bg-white/90 mt-5">
                  Pagar agora
                </Button>
              </div>
            )}

            <div className="eyebrow mb-3">Histórico</div>
            <table className="w-full text-sm">
              <thead className="text-slate-500 eyebrow">
                <tr>
                  <th className="text-left py-2">Vencimento</th>
                  <th className="text-left py-2">Descrição</th>
                  <th className="text-left py-2">Valor</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.invoice_id} className="border-t border-[var(--line)]">
                    <td className="py-2">{fmtDate(i.due_date)}</td>
                    <td className="py-2 text-slate-600">{i.type === "ativacao" ? "1ª parcela — ativação" : "Fatura mensal"}</td>
                    <td className="py-2 mono">{fmtBRL(i.amount)}</td>
                    <td className="py-2"><span className={`status-pill ${STATUS_CLASS[i.status]}`}>{STATUS_LABELS[i.status]}</span></td>
                    <td className="py-2">
                      {i.status !== "pago" && <Button size="sm" onClick={() => setPayFor(i)} className="btn-primary" data-testid={`portal-pay-${i.invoice_id}`}>Pagar</Button>}
                    </td>
                  </tr>
                ))}
                {data.invoices.length === 0 && <tr><td colSpan="5" className="py-6 text-center text-slate-400">Sem faturas ainda.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Certificate tab */}
        {tab === "certificate" && (
          <div>
            <div className="flex justify-end mb-3">
              <Button onClick={() => window.print()} data-testid="portal-download-cert" className="btn-primary">
                <Download size={14} className="mr-2" /> Baixar / Imprimir
              </Button>
            </div>
            <div className="bg-white border border-[var(--line)] rounded-lg p-12 relative overflow-hidden" data-testid="portal-cert-doc">
              <div className="absolute top-4 right-4 opacity-5"><ShieldCheck size={220} weight="duotone" /></div>
              <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_locacao-shield/artifacts/5k0zetdw_logo%20safimob%2003.JPG" alt="Safimob" className="h-14" />
              <div className="mt-8 border-t border-b border-[var(--line)] py-6">
                <div className="eyebrow">Certificado nº</div>
                <div className="mono text-lg mt-1">CRT-{c.contract_id.toUpperCase()}</div>
                <div className="font-display text-3xl md:text-4xl tracking-tight mt-6">Este certificado atesta que</div>
                <div className="font-display text-4xl md:text-5xl tracking-tighter mt-3 text-[var(--emerald)]">{c.tenant_name}</div>
                <div className="mt-6 text-slate-700 leading-relaxed max-w-2xl">
                  possui garantia locatícia aprovada pela <strong>Safimob Garantidora de Aluguel</strong> para o imóvel em{" "}
                  <strong>{c.property_address}</strong>, sob plano <strong>{PLAN_LABELS[c.plan]}</strong>, com cobertura de até{" "}
                  <strong className="text-[var(--emerald)]">{c.coverage_multiplier}x</strong> o valor mensal do aluguel.
                </div>
              </div>
              <div className="grid grid-cols-3 gap-6 mt-6">
                <div><div className="eyebrow">Aluguel</div><div className="mono text-lg mt-1">{fmtBRL(c.rent_amount)}</div></div>
                <div><div className="eyebrow">Cobertura total</div><div className="mono text-lg mt-1">{fmtBRL(c.rent_amount * c.coverage_multiplier)}</div></div>
                <div><div className="eyebrow">Emitido em</div><div className="mono text-lg mt-1">{fmtDate(c.start_date)}</div></div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Payment dialog */}
      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Pagar fatura</DialogTitle></DialogHeader>
          {payFor && (
            <>
              <div className="my-2">
                <div className="metric-value text-3xl">{fmtBRL(payFor.amount)}</div>
                <div className="text-sm text-slate-500 mt-1">Vencimento {fmtDate(payFor.due_date)}</div>
              </div>
              {payFor.provider === "INTER" ? (
                <InvoicePayment invoice={payFor} onSync={syncInvoice} />
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <Button data-testid="portal-method-pix" onClick={() => pay("pix")} className="btn-primary h-14 flex-col gap-0.5">PIX<span className="text-[10px] opacity-70">Instantâneo</span></Button>
                    <Button data-testid="portal-method-boleto" onClick={() => pay("boleto")} variant="outline" className="h-14 flex-col gap-0.5">Boleto<span className="text-[10px] opacity-70">1-3 dias</span></Button>
                    <Button data-testid="portal-method-cartao" onClick={() => pay("cartao")} variant="outline" className="h-14 flex-col gap-0.5">Cartão<span className="text-[10px] opacity-70">Crédito</span></Button>
                  </div>
                  <InvoicePayment invoice={payFor} />
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sign dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Assinatura eletrônica</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-3">
            Ao assinar, você concorda com os termos do contrato de garantia locatícia da Safimob para o imóvel em{" "}
            <strong>{c.property_address}</strong>, plano <strong>{PLAN_LABELS[c.plan]}</strong>.
          </div>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600 max-h-32 overflow-y-auto">
            Este contrato prevê cobertura de até <strong>{c.coverage_multiplier}x</strong> o valor do aluguel, com vigência de{" "}
            {c.duration_months} meses. A garantia entra em vigor após esta assinatura e a confirmação do pagamento da 1ª parcela.
          </div>
          <Input data-testid="portal-signature" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Digite seu nome completo" className="font-display italic text-lg mt-3" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Cancelar</Button>
            <Button className="btn-primary" onClick={sign} data-testid="portal-sign-confirm"><PenNib size={14} className="mr-2" /> Assinar & Ativar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Info({ label, value, hi }) {
  return (
    <div className={`p-3 rounded-md border ${hi ? "bg-[var(--emerald-soft)] border-emerald-200" : "bg-slate-50 border-slate-100"}`}>
      <div className="eyebrow">{label}</div>
      <div className={`metric-value text-xl mt-1 ${hi ? "text-[var(--emerald)]" : ""}`}>{value}</div>
    </div>
  );
}
function PortalError({ message }) {
  return (
    <div className="min-h-screen bg-[var(--bone)] flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <Warning size={48} className="mx-auto text-red-600" weight="duotone" />
        <h1 className="font-display text-3xl mt-4">Link inválido</h1>
        <p className="text-slate-600 mt-2">{message} Solicite um novo link à imobiliária.</p>
      </div>
    </div>
  );
}
